import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, inject, input, numberAttribute, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { BodyClassService } from '@df/ui/body-class.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';

@Component({
    selector: 'back-to-top',
    templateUrl: './back-to-top.html',
    styles: [
        `
            :host:not(.initialized) {
                display: none;
            }
        `
    ],
    standalone: true,
    imports: [CommonModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BackToTopComponent extends UiComponent implements OnInit, OnDestroy {
    /**
     * The height at which the page starts
     */
    readonly top = input<number, string | number>(0, { transform: numberAttribute });

    protected forbiddenLocations: string[] = [];

    protected location = inject(Location);
    protected bodyClassService = inject(BodyClassService);

    @HostBinding('class.initialized')
    private initializedClass = false;

    backToTop!: () => void;
    private _canShow?: boolean;

    protected elementRef = inject(ElementRef);
    protected renderer = inject(Renderer2);
    protected meta = inject(Meta);
    protected app = inject(App);

    private intersectionObserver?: IntersectionObserver;
    private sentinelElement?: HTMLElement;

    override ngOnInit() {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.backToTop = this.timeoutService.throttle(() => {
            const scrollingElement = this.getScrollingElement();

            if (scrollingElement) {
                scrollingElement.scrollTo({
                    top: 0,
                    left: 0
                });
            }
        }, 1000);

        this.setupIntersectionObserver();

        this.subscriptions.add(
            this.meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    this.setupIntersectionObserver();
                }
            })
        );

        this.initializedClass = true;
        this.updateVisibility(false);

        super.ngOnInit();
    }

    override ngOnDestroy() {
        this.cleanupIntersectionObserver();
        super.ngOnDestroy();
    }

    protected getScrollingElement(): Element {
        return this.client.getRootElement();
    }

    /**
     * Display component if you are not in forbidden location
     * Returns cached value to avoid layout recalculation
     */
    get canShow(): boolean {
        return !!this._canShow;
    }

    /**
     * Setup IntersectionObserver to watch for sentinel element visibility
     */
    private setupIntersectionObserver(): void {
        this.cleanupIntersectionObserver();

        // Check if we're in a forbidden location
        if (this.forbiddenLocations?.includes(this.location.path())) {
            this.updateVisibility(false);
            return;
        }

        // Create or get sentinel element
        this.createSentinelElement();

        if (!this.sentinelElement) {
            return;
        }

        // Calculate rootMargin based on top input
        const top = this.top();
        const rootMargin = top > 0 ? `${top}px 0px 0px 0px` : '0px';

        // Create IntersectionObserver
        this.intersectionObserver = new IntersectionObserver(
            entries => {
                // When sentinel is not visible (scrolled past), show the button
                const isVisible = entries.some(entry => entry.isIntersecting);
                this.updateVisibility(!isVisible);
            },
            {
                root: null,
                rootMargin: rootMargin,
                threshold: 0
            }
        );

        this.intersectionObserver.observe(this.sentinelElement);
    }

    /**
     * Create a sentinel element at the top of the page
     */
    private createSentinelElement(): void {
        const scrollingElement = this.getScrollingElement();
        if (!scrollingElement) {
            return;
        }

        // Remove existing sentinel if any
        const existingSentinel = document.getElementById('back-to-top-sentinel');
        if (existingSentinel) {
            existingSentinel.remove();
        }

        // Create new sentinel element
        this.sentinelElement = this.renderer.createElement('div');
        this.renderer.setAttribute(this.sentinelElement, 'id', 'back-to-top-sentinel');
        this.renderer.setStyle(this.sentinelElement, 'position', 'absolute');
        this.renderer.setStyle(this.sentinelElement, 'top', '0');
        this.renderer.setStyle(this.sentinelElement, 'left', '0');
        this.renderer.setStyle(this.sentinelElement, 'width', '1px');
        this.renderer.setStyle(this.sentinelElement, 'height', '1px');
        this.renderer.setStyle(this.sentinelElement, 'pointer-events', 'none');
        this.renderer.setStyle(this.sentinelElement, 'visibility', 'hidden');

        // Insert at the beginning of the scrolling element
        if (scrollingElement === document.documentElement || scrollingElement === document.body) {
            this.renderer.insertBefore(document.body, this.sentinelElement, document.body.firstChild);
        } else {
            this.renderer.insertBefore(scrollingElement, this.sentinelElement, scrollingElement.firstChild);
        }
    }

    /**
     * Clean up IntersectionObserver and sentinel element
     */
    private cleanupIntersectionObserver(): void {
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = undefined;
        }

        if (this.sentinelElement) {
            this.renderer.removeChild(this.sentinelElement.parentNode, this.sentinelElement);
            this.sentinelElement = undefined;
        }
    }

    /**
     * Update component visibility based on intersection state
     */
    private updateVisibility(shouldShow: boolean): void {
        if (this._canShow === shouldShow) {
            return;
        }

        this._canShow = shouldShow;

        if (!shouldShow) {
            this.renderer.addClass(this.elementRef.nativeElement, 'ng-hide');
        } else {
            this.renderer.removeClass(this.elementRef.nativeElement, 'ng-hide');
        }

        this.bodyClassService.set('is-back-to-top', this, shouldShow);
    }
}
